<template>
  <v-snackbar
    v-model="visible"
    :timeout="-1"
    location="bottom"
    color="surface"
    class="pwa-install-snackbar"
    multi-line
  >
    <!-- iOS: no programmatic prompt, so guide the manual gesture. -->
    <div v-if="showIosHint" class="pwa-install-content">
      <v-icon class="pwa-install-app-icon" color="primary">mdi-cellphone-arrow-down</v-icon>
      <div class="pwa-install-text">
        <span class="pwa-install-title">Install LensPoint</span>
        <span class="pwa-install-subtitle">
          Tap <v-icon size="16" class="pwa-inline-icon">mdi-export-variant</v-icon> Share, then
          <strong>Add to Home Screen</strong>.
        </span>
      </div>
    </div>

    <!-- Android / desktop: one-tap native install. -->
    <div v-else class="pwa-install-content">
      <v-icon class="pwa-install-app-icon" color="primary">mdi-cellphone-arrow-down</v-icon>
      <div class="pwa-install-text">
        <span class="pwa-install-title">Install LensPoint</span>
        <span class="pwa-install-subtitle">
          Add it to your home screen for a full-screen, app-like experience.
        </span>
      </div>
    </div>

    <template #actions>
      <v-btn variant="text" size="small" @click="onDismiss">Not now</v-btn>
      <v-btn
        v-if="canPrompt"
        variant="elevated"
        color="primary"
        size="small"
        :loading="installing"
        @click="onInstall"
      >
        Install
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { usePwaInstall } from '@/composables/usePwaInstall'
import { globalState } from '@/stores/globalState'

// Delay before surfacing the nudge so it doesn't pop the instant the app loads.
const SHOW_DELAY_MS = 2500

const { canInstall, canPrompt, showIosHint, promptInstall, dismiss } = usePwaInstall()

const installing = ref(false)
const delayElapsed = ref(false)
const open = ref(true)

const isAuthed = computed(() => globalState.isAuthenticated)

// Only nudge engaged (signed-in) users, and only once the delay has passed.
const visible = computed({
  get: () => open.value && delayElapsed.value && isAuthed.value && canInstall.value,
  set: (v: boolean) => {
    if (!v) open.value = false
  },
})

let delayTimer: ReturnType<typeof setTimeout> | null = null
watch(
  () => canInstall.value && isAuthed.value,
  (ready) => {
    if (ready && !delayElapsed.value && !delayTimer) {
      delayTimer = setTimeout(() => {
        delayElapsed.value = true
        delayTimer = null
      }, SHOW_DELAY_MS)
    }
  },
  { immediate: true },
)

async function onInstall() {
  installing.value = true
  try {
    await promptInstall()
  } finally {
    installing.value = false
    open.value = false
  }
}

function onDismiss() {
  dismiss()
  open.value = false
}
</script>

<style scoped>
.pwa-install-content {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.pwa-install-app-icon {
  font-size: 26px;
  flex-shrink: 0;
}

.pwa-install-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pwa-install-title {
  font-weight: 600;
  font-size: 14px;
}

.pwa-install-subtitle {
  font-size: 12px;
  opacity: 0.8;
  line-height: 1.4;
}

.pwa-inline-icon {
  vertical-align: text-bottom;
  opacity: 0.9;
}
</style>
